<template>
  <teleport to="#modals">
    <div
      ref="draggable"
      class="fixed z-50 w-[520px] touch-none rounded-sm border-2 border-gray-100 bg-white shadow-sm select-none"
      :style="{
        top: `${restrictedY}px`,
        left: `${restrictedX}px`,
      }"
      data-cy="custom-attribute-preview-wrapper"
    >
      <div class="space-y-3">
        <div class="flex items-center justify-between px-6 pt-3">
          <lf-h3 data-cy="attribute-name">
            {{ $t("ORCHESTRATION.NEW_ATTRIBUTE") }}
          </lf-h3>
          <div class="flex items-center space-x-4">
            <icon-base
              class="cursor-pointer hover:text-gray-500"
              :icon="IconClear"
              data-cy="button-close"
              @click="$emit('close')"
            />
          </div>
        </div>
        <hr />
        <div class="p-4" data-cy="custom-attribute-formula">
          <lf-input
            name="attribute-name"
            :placeholder="$t('COMMON.NAME')"
            @key-released="attributeName = $event"
          />
          <formula-editor
            v-model="formula"
            :placeholder="$t('ATTRIBUTE_BUILDER.PLACEHOLDER')"
          />
        </div>
        <hr />
        <div class="px-6 pt-2 pb-6">
          <primary-button
            class="w-full"
            :disabled="isSubmitting || orchestrationLoading"
            @click="submit"
          >
            {{ $t("ORCHESTRATION.PUBLISH") }}
          </primary-button>
        </div>
      </div>
    </div>
  </teleport>
</template>
<script setup lang="ts">
import {
  clamp,
  useDraggable,
  useElementBounding,
  useWindowSize,
} from "@vueuse/core";
import { computed, ref, useTemplateRef } from "vue";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import {
  CustomAttributeStatusOption,
  CustomAttributeTypeOption,
} from "@/enums/customAttributes";
import { ServiceType } from "@/enums/customAttributesV2";
import { useFormulaEditor } from "@/hooks/attributeBuilder";
import { usePromiseWrapper } from "@/hooks/common";
import attributeBuilderService from "@/services/modules/attributeBuilder";
import { useAttributeBuilderStore } from "@/stores/customAttributeBuilder";

import IconClear from "../icons/IconClear.vue";
import FormulaEditor from "@/components/customAttributesV2/FormulaEditor.vue";
import PrimaryButton from "@/components/ui/buttons/PrimaryButton.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";

import type { EditorNode } from "@/models/TextEditor";
import type { CustomAttribute } from "@/models/customAttributes/customAttribute";

const {
  serviceDataType = ServiceType.ORCHESTRATION,
  initialX,
  initialY,
} = defineProps<{
  serviceDataType?: ServiceType;
  initialX?: number;
  initialY?: number;
}>();

const emit = defineEmits<{
  close: [void];
}>();

const attributeBuilderStore = useAttributeBuilderStore();
const { services } = storeToRefs(attributeBuilderStore);
const { getServices } = attributeBuilderStore;

const { getters, dispatch } = useStore();

const { width: windowWidth, height: windowHeight } = useWindowSize();
const { infixToPrefix, convertToJson } = useFormulaEditor();

const draggable = useTemplateRef("draggable");
const formula = ref<EditorNode[]>([]);
const attributeName = ref("");

const { width, height } = useElementBounding(draggable);
const { x, y } = useDraggable(draggable, {
  initialValue: {
    x: initialX ?? windowWidth.value / 2,
    y: initialY ?? windowHeight.value / 2,
  },
  preventDefault: true,
  onStart: (_, event) => {
    const target = event.target as HTMLElement;
    const isInputElement = target.closest(
      'input, textarea, [data-cy="formula-editor"], .formula-editor'
    );
    if (isInputElement) {
      return false;
    }
  },
});

const attributeBuilderLatestSchemaVersion = computed<string>(
  () => getters["options/attributeSchemaVersion"]
);

const restrictedX = computed(() =>
  clamp(0, x.value, windowWidth.value - width.value)
);
const restrictedY = computed(() =>
  clamp(0, y.value, windowHeight.value - height.value)
);

const orchestrationLoading = computed(() => getters["orchestration/loading"]);

const { fetchWrapper: submitAttribute, loading: isSubmitting } =
  usePromiseWrapper(async (payload: Partial<CustomAttribute>) => {
    await attributeBuilderService.createAttribute(payload);
    if (serviceDataType === ServiceType.ORCHESTRATION) {
      await dispatch("orchestration/getDataServices");
    } else if (serviceDataType === ServiceType.SCORECARD) {
      await dispatch("scorecards/getServices", { showLoader: false });
    }
    emit("close");
  });

const submit = async () => {
  const prefixNotation = infixToPrefix(formula.value);
  const JSON = convertToJson(prefixNotation);
  const payload: Partial<CustomAttribute> = {
    name: attributeName.value,
    type: CustomAttributeTypeOption.formula,
    status: CustomAttributeStatusOption.published,
    content: {
      version: attributeBuilderLatestSchemaVersion.value,
      expression: JSON,
    },
    is_legacy: false,
    tokens: formula.value,
  };
  await submitAttribute(payload);
};

if (!services.value.length) {
  getServices();
}
</script>
<style scoped>
@reference "@/assets/tailwind.css";

:deep([data-cy="formula-editor"]) {
  @apply max-h-50-screen overflow-auto border-gray-200;
}
</style>
